/**
 * Bohemia's vanilla mission files, bundled under public/vanilla/ by
 * scripts/build_vanilla_data.py so the editor works without any upload.
 */
import { parseLootData, type LootData, type LootJson } from './loot';

export type VanillaFile = { fileName: string; label: string; visible: boolean; loadByDefault: boolean };

export const VANILLA_FILES: VanillaFile[] = [
  { fileName: 'types.xml', label: 'Loot economy', visible: true, loadByDefault: true },
  { fileName: 'events.xml', label: 'Event economy', visible: true, loadByDefault: true },
  { fileName: 'cfgeventspawns.xml', label: 'Event positions', visible: false, loadByDefault: true },
  { fileName: 'cfgplayerspawnpoints.xml', label: 'Player spawns', visible: false, loadByDefault: true },
  { fileName: 'zombie_territories.xml', label: 'Infected', visible: false, loadByDefault: true },
  { fileName: 'wolf_territories.xml', label: 'Wolves', visible: false, loadByDefault: true },
  { fileName: 'bear_territories.xml', label: 'Bears', visible: false, loadByDefault: true },
  { fileName: 'red_deer_territories.xml', label: 'Red deer', visible: false, loadByDefault: false },
  { fileName: 'roe_deer_territories.xml', label: 'Roe deer', visible: false, loadByDefault: false },
  { fileName: 'wild_boar_territories.xml', label: 'Wild boar', visible: false, loadByDefault: false },
  { fileName: 'pig_territories.xml', label: 'Pigs', visible: false, loadByDefault: false },
  { fileName: 'cattle_territories.xml', label: 'Cattle', visible: false, loadByDefault: false },
  { fileName: 'sheep_goat_territories.xml', label: 'Sheep & goats', visible: false, loadByDefault: false },
  { fileName: 'hen_territories.xml', label: 'Hens', visible: false, loadByDefault: false },
  { fileName: 'hare_territories.xml', label: 'Hares', visible: false, loadByDefault: false },
  { fileName: 'fox_territories.xml', label: 'Foxes', visible: false, loadByDefault: false },
];

function url(folder: string, file: string): string {
  return `${import.meta.env.BASE_URL}${folder}/${file}`;
}

export async function fetchVanillaFiles(
  folder: string,
  files: VanillaFile[],
): Promise<{ fileName: string; content: string; visible: boolean }[]> {
  return Promise.all(
    files.map(async (file) => {
      const response = await fetch(url(folder, file.fileName));
      if (!response.ok) throw new Error(`${file.fileName}: HTTP ${response.status}`);
      return { fileName: file.fileName, content: await response.text(), visible: file.visible };
    }),
  );
}

export async function fetchLootData(folder: string): Promise<LootData> {
  const response = await fetch(url(folder, 'loot.json'));
  if (!response.ok) throw new Error(`loot.json: HTTP ${response.status}`);
  return parseLootData((await response.json()) as LootJson);
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Could not load ${src}`));
    image.src = src;
  });
}

export function vanillaImageUrl(folder: string, file: 'map.jpg' | 'tiers.png'): string {
  return url(folder, file);
}
